import { Divider, Typography } from '@mui/material';
import { LP_INVESTMENT_LIST } from 'adminConstants/routes';
import Page from 'components/new/Page';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { CalendarDay } from 'further-ui/utils';
import { numberToCurrencyString } from 'further-ui/utils';
import { Column, Row } from 'further-ui/components/Layout';
import StaticField from 'further-ui/components/StaticField';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import api from 'lib/trpcClient';
import React from 'react';
import { useParams } from 'react-router-dom';

const ViewInvestmentReversalPage: React.FC<{ vehicleId: string }> = ({
  vehicleId,
}) => {
  const { actionId } = useParams<{ actionId: string }>();
  const { transactionTypeOptions } = useTransactionTypeSelectorOptions();
  const { vehicle } = useLayoutContext();

  const [existingReversal] = api.lpReversal.getReversal.useSuspenseQuery({
    vehicleId,
    actionId: actionId ?? '',
  });

  const [investmentData] = api.lpInvestment.get.useSuspenseQuery({
    investmentId: actionId ?? '',
    vehicleId,
  });

  const reversalLedgerDate = existingReversal
    ? existingReversal.ledgerDate
    : new CalendarDay(new Date());

  const [reversalTransactions] =
    api.lpReversal.getTransactions.useSuspenseQuery({
      vehicleId,
      actionId: actionId ?? '',
      reversalDate: reversalLedgerDate,
    });

  const forceShowTcy =
    !!vehicle && investmentData?.currencyCode !== vehicle.baseCurrency;

  return (
    <Page
      tabTitle="View Investment Reversal"
      spacing="lg"
      heading="View Investment Reversal"
      withHeadingDivider
      goBackAction={LP_INVESTMENT_LIST}
    >
      <Column spacing="lg">
        <StaticField label="Reversal date">
          {reversalLedgerDate.toReadable()}
        </StaticField>

        <Divider />

        <Typography variant="h6">Original investment</Typography>

        <Row spacing="lg">
          <StaticField label="Deal name">
            {investmentData?.dealName ?? ''}
          </StaticField>
          <StaticField label="Amount">
            {numberToCurrencyString(
              investmentData?.totalInvestmentAmountTcy ?? 0,
              { currency: investmentData?.currencyCode },
            )}
          </StaticField>
          <StaticField label="Deal completion date">
            {investmentData?.dealCompletionDate?.toReadable() ?? ''}
          </StaticField>
        </Row>

        <Divider />

        <Typography variant="h6">Account transactions</Typography>

        {vehicle && (
          <TransactionAllocator
            readOnly
            transactions={reversalTransactions}
            transactionTypeOptions={transactionTypeOptions}
            transactionCurrency={
              investmentData?.currencyCode ?? vehicle.baseCurrency
            }
            vehicle={{
              _id: vehicleId,
              baseCurrency: vehicle.baseCurrency,
              fxDataFeed: vehicle.fxDataFeed,
            }}
            fxRate={investmentData?.currencyRate ?? null}
            forceShowTcy={forceShowTcy}
          />
        )}
      </Column>
    </Page>
  );
};

export default ViewInvestmentReversalPage;
